import React, { useCallback, useContext, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Breadcrumbs } from '@/components/ui/breadcrumb'
import { Box } from 'components/Box'
import ConfirmationModal from 'components/ConfirmationModal/ConfirmationModal'
import { Loading } from 'components/Loading'
import { Spinner } from 'components/Spinner'
import { ToastContext } from 'components/Toast/Toast'
import PageNotFound from 'routes/PageNotFound/PageNotFound'
import { AffiliateNavigation } from 'routes/Records/Affiliates/RecordsAffiliates/AffiliateNavigations'
import { AffiliateApiClient } from 'utils/api/affiliateApi'
import { useApiClient } from 'utils/api/ApiClient'
import { useCallbackWithForceRefresh } from 'utils/api/useCallbackWithForceRefresh'
import { useFetchOne } from 'utils/api/useFetch'
import { AuthSessionContext } from 'utils/AuthContext'
import { affiliateDetailsRouteUrl, links } from 'utils/featureToggles'

import { AffiliateClientsPage } from '../AffiliateClientsPage'
import { AffiliateUpdateDto } from './AffiliateDetailsForm'
import { AffiliateDetailsPage } from './AffiliateDetailsPage'

export enum AffiliateTabs {
  DETAILS = 'details',
  CLIENTS = 'clients',
}

export const AffiliateSlug = (): JSX.Element => {
  const [auth] = useContext(AuthSessionContext)
  const permissions = auth?.permissions || []

  const { id, slug } = useParams<{ id: string; slug: AffiliateTabs }>()
  const affiliateApiClient = useApiClient(AffiliateApiClient)
  const setToast = useContext(ToastContext)
  const [crumbs, setCrumbs] = useState<{ title: string; link: string }[]>([
    { title: 'Affiliate', link: links.recordsAffiliates.url as string },
  ])
  const [isEdit, setEdit] = useState(false)
  const [confirmationModal, setConfirmationModal] = useState<boolean>(false)
  const [navigationLink, setNavigationLink] = useState<string | null>(null)
  const navigate = useNavigate()

  const { callback: fetchData, forceRefresh } = useCallbackWithForceRefresh(
    () => (id ? affiliateApiClient.getAffiliate(id) : Promise.resolve(null)),
    []
  )
  const { data, isLoading } = useFetchOne(fetchData)

  const matchesSlug = (tab?: string) => {
    if (!tab) {
      return false
    }
    return tab === AffiliateTabs.DETAILS || tab === AffiliateTabs.CLIENTS
  }

  const updateAffiliate = useCallback(async (id: string, data: AffiliateUpdateDto) => {
    await affiliateApiClient.updateAffiliate(id, data)
    setToast({ type: 'success', title: 'Affiliate has been updated.' })
    forceRefresh()
    setEdit(false)
  }, [])

  useEffect(() => {
    if (data) {
      const [crumb1, crumb2] = [
        ...crumbs,
        {
          title: data?.externalId || '',
          link: affiliateDetailsRouteUrl(data, AffiliateTabs.DETAILS),
        },
      ]
      setCrumbs([crumb1, crumb2])
    }
  }, [data]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleBreadcrumbClick = (link: string) => {
    if (isEdit) {
      setNavigationLink(link)
      setConfirmationModal(true)
    } else {
      navigate(link)
    }
  }

  const handleBreadcrumbNavigation = (shouldNavigate: boolean) => {
    if (shouldNavigate && navigationLink) {
      setEdit(false)
      navigate(navigationLink)
    }
    setConfirmationModal(false)
    setNavigationLink(null)
  }

  const innerRender = () => {
    if (!matchesSlug(slug)) {
      return <PageNotFound />
    }
    if (isLoading) {
      return <Spinner />
    }
    if (!data) {
      return <PageNotFound />
    }
    if (slug === AffiliateTabs.DETAILS) {
      return (
        <React.Fragment>
          <Breadcrumbs crumbs={crumbs} />
          <AffiliateDetailsPage
            data={data}
            permissions={permissions}
            forceRefresh={forceRefresh}
            onSubmit={updateAffiliate}
          />
        </React.Fragment>
      )
    }
    if (slug === AffiliateTabs.CLIENTS) {
      return (
        <React.Fragment>
          <Box display='flex' alignItems='baseline'>
            <Breadcrumbs crumbs={crumbs} onCrumbClick={handleBreadcrumbClick} />
          </Box>
          <AffiliateNavigation data={data} isEdit={isEdit} setEdit={setEdit} />
          <AffiliateClientsPage />
        </React.Fragment>
      )
    }
    return <PageNotFound />
  }

  return (
    <Loading isLoading={isLoading && !matchesSlug(slug)}>
      {innerRender()}
      <ConfirmationModal
        contentText='The page is asking you to confirm that you want to leave - data you have entered may not be saved.'
        yesButtonText='Leave Page'
        cancelButtonText='Stay on page'
        onYes={() => handleBreadcrumbNavigation(true)}
        onCancel={() => handleBreadcrumbNavigation(false)}
        visible={confirmationModal}
      />
    </Loading>
  )
}
